import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { mapBy } from '../../../../../../../shared/lib/functional';
import {
  DailyProgramEvent,
  DailyProgramOffer,
  DailyProgramVenue,
} from '../../../../../../../shared/types/dailyProgram';
import LoadingPage from '../views/LoadingPage';
import { useData } from './DataContext';

interface StoredFavorites {
  events: { [key: string]: string };
  venues: { [key: string]: string };
  offers: { [key: string]: string };
}

interface Favorites {
  events: { [key: string]: DailyProgramEvent };
  venues: { [key: string]: DailyProgramVenue };
  offers: { [key: string]: DailyProgramOffer };
}

type ToggleFavoriteParams =
  | { type: 'events'; value: DailyProgramEvent }
  | { type: 'venues'; value: DailyProgramVenue }
  | { type: 'offers'; value: DailyProgramOffer };

interface FavoritesContextProps {
  favorites: Favorites;
  toggleFavorite: (params: ToggleFavoriteParams) => void;
}

const LOCAL_STORAGE_KEY = '@GO_NAVEGANTE_FAVORITES_V3';
const OLD_LS_KEYS = ['@GO_NAVEGANTE_FAVORITES', '@GO_NAVEGANTE_FAVORITES_V2'];
const FAVORITES_UPPER_LIMIT = 3;

const FavoritesContext = createContext<FavoritesContextProps | undefined>(undefined);

function GENERATE_DEFAULT_FAVORITES() {
  return { events: {}, venues: {}, offers: {} };
}

function getStoredFavorites(): StoredFavorites {
  let result: StoredFavorites = GENERATE_DEFAULT_FAVORITES();

  try {
    const lsCopy = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (lsCopy) {
      result = JSON.parse(lsCopy) as StoredFavorites;
    }
  } catch (error) {
    console.error('Error getting stored favorites', error);
  }

  return result;
}

function trimToLimit(obj: { [key: string]: string }, limit: number) {
  const entries = Object.entries(obj);
  if (entries.length <= limit) return obj;
  const sorted = entries.sort((a, b) => Number(b[1]) - Number(a[1])).slice(0, limit);
  return Object.fromEntries(sorted);
}

export function WithFavorites({ children }: { children: ReactNode }) {
  const { data, todayLineup } = useData();

  const [favorites, setFavorites] = useState<Favorites | undefined>(undefined);

  useEffect(() => {
    OLD_LS_KEYS.forEach(k => localStorage.removeItem(k));

    const current = getStoredFavorites();
    const copy: Favorites = GENERATE_DEFAULT_FAVORITES();

    const allEventsMap = mapBy(
      Object.values(data.lineup)
        .map(l => l.events)
        .flat(),
      'id'
    );
    const allVenuesMap = mapBy(todayLineup.venues, 'templateId');
    const allOffersMap = mapBy(todayLineup.offers, 'id');

    current.events = trimToLimit(current.events, FAVORITES_UPPER_LIMIT);
    current.venues = trimToLimit(current.venues, FAVORITES_UPPER_LIMIT);
    current.offers = trimToLimit(current.offers, FAVORITES_UPPER_LIMIT);

    Object.keys(current.events).forEach(key => {
      const item = allEventsMap[key];
      if (item) copy.events[key] = item;
    });

    Object.keys(current.venues).forEach(key => {
      const item = allVenuesMap[key];
      if (item) copy.venues[key] = item;
    });

    Object.keys(current.offers).forEach(key => {
      const item = allOffersMap[key];
      if (item) copy.offers[key] = item;
    });

    setFavorites(copy);
  }, []);

  useEffect(() => {
    if (!favorites) return;

    const copy: StoredFavorites = GENERATE_DEFAULT_FAVORITES();
    const old: StoredFavorites = getStoredFavorites();
    const date = Date.now().toString();

    // here we update the timestamps. ls only stores ids + timestamps.
    // we convert that into favorites by looking up the ids in the data context
    // if an event/venue/offer is no longer present in the data, it will
    // be removed from favorites only if the user unfavorites it or if
    // the upper limit is reached

    Object.keys(favorites.events).forEach(key => {
      copy.events[key] = old.events[key] || date;
    });

    Object.keys(favorites.venues).forEach(key => {
      copy.venues[key] = old.venues[key] || date;
    });

    Object.keys(favorites.offers).forEach(key => {
      copy.offers[key] = old.offers[key] || date;
    });

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(copy));
  }, [favorites]);

  function toggleFavorite(params: ToggleFavoriteParams) {
    if (!favorites) return;

    const { type, value } = params;
    const copy = { ...favorites };

    if (type === 'events') {
      if (copy.events[value.id]) {
        delete copy.events[value.id];
      } else if (value) {
        copy.events[value.id] = value;
      }
    } else if (type === 'venues') {
      if (copy.venues[value.templateId]) {
        delete copy.venues[value.templateId];
      } else if (value) {
        copy.venues[value.templateId] = value;
      }
    } else if (type === 'offers') {
      if (copy.offers[value.id]) {
        delete copy.offers[value.id];
      } else if (value) {
        copy.offers[value.id] = value;
      }
    }

    setFavorites(copy);
  }

  if (!favorites) return <LoadingPage />;

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
